import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import usePages from "../../hooks/usePages";
import Loading from "../../components/Loading";
import ItemForm from "../../components/ItemForm";
import { overlays } from "../../components/OverlayModuleList";

const STORAGE_PREFIX = "dashboard_module_";

export default function Dashboard() {
    const { pages, loading, error } = usePages();

    const [selectedPage, setSelectedPage] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedModule, setSelectedModule] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editItem, setEditItem] = useState(null);

    /* =========================
       localStorage helpers
    ========================= */

    const clearModulesFromStorage = () => {
        Object.keys(localStorage)
            .filter(key => key.startsWith(STORAGE_PREFIX))
            .forEach(key => localStorage.removeItem(key));
    };

    const saveModuleToStorage = (module) => {
        clearModulesFromStorage();
        localStorage.setItem(
            `${STORAGE_PREFIX}${module.id}`,
            JSON.stringify(module)
        );
    };

    const getModuleFromStorage = (id) => {
        const data = localStorage.getItem(`${STORAGE_PREFIX}${id}`);
        return data ? JSON.parse(data) : null;
    };

    /* =========================
       Initial page selection
    ========================= */

    useEffect(() => {
        if (pages && pages.length > 0 && !selectedPage) {
            setSelectedPage(pages[0]);
        }
    }, [pages, selectedPage]);

    /* =========================
       Reset module when page changes
    ========================= */

    useEffect(() => {
        const storedModuleKey = selectedModule ? `${STORAGE_PREFIX}${selectedModule.id}` : null;
        const storedModuleExists = storedModuleKey ? localStorage.getItem(storedModuleKey) : false;

        Object.keys(localStorage)
            .filter(key => key.startsWith(STORAGE_PREFIX))
            .forEach(key => {
                if (!storedModuleExists || key !== storedModuleKey) {
                    localStorage.removeItem(key);
                }
            });
    }, [selectedPage, selectedModule]);

    if (loading) return <Loading message="Chargement du dashboard..." />;
    if (error) return <p className="p-8 text-red-600">Erreur : {error.message}</p>;

    /* =========================
       Overlay resolver
    ========================= */

    const getOverlayComponent = (overlayName) => {
        if (!overlayName) return overlays.DEFAULT || (() => null);
        return overlays[overlayName] || overlays.DEFAULT || (() => <div>Overlay introuvable</div>);
    };

    /* =========================
       Update module (form submit)
    ========================= */

    const updateModule = (id, data) => {
        const updatedModule = { ...selectedModule, items: data };

        // Mettre à jour le localStorage
        saveModuleToStorage(updatedModule);

        // Mettre à jour le state pour réafficher le module
        setSelectedModule(updatedModule);
        setEditItem(updatedModule);
        setSelectedPage(prev => ({
            ...prev,
            modules: prev.modules.map(m => m.id === id ? updatedModule : m)
        }));

        // Fermer la modale
        setShowEditModal(false);
    };

    const applyChangesToDatabase = async () => {
        if (!selectedModule?.items) return;

        try {
            const updatePromises = selectedModule.items.map(item => {
                const formData = new FormData();

                // Champs texte / string
                ['title', 'description', 'alt_picture1', 'alt_picture2', 'link'].forEach(field => {
                    if (item[field] !== undefined && item[field] !== null) {
                        formData.append(field, item[field]);
                    }
                });

                // Images : n'envoyer que si c'est un vrai File
                if (item.picture1File instanceof File) {
                    formData.append('picture1', item.picture1File);
                }
                if (item.picture2File instanceof File) {
                    formData.append('picture2', item.picture2File);
                }

                formData.append("_method", "PUT");

                return api.post(`/api/items/${item.id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            });

            const results = await Promise.all(updatePromises);

            // Laravel renvoie les chemins string pour les images
            const updatedItems = results.map(res => res.data);
            const updatedModule = { ...selectedModule, items: updatedItems };

            // Mise à jour du state et du localStorage
            setSelectedModule(updatedModule);
            saveModuleToStorage(updatedModule);

            alert("Modifications enregistrées avec succès !");
        } catch (err) {
            console.error(err.response?.data || err);
            alert("Erreur lors de l'envoi des modifications");
        }
    };


    /* =========================
       Modal component
    ========================= */

    function Modal({ open, onClose, children }) {
        if (!open) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-lg shadow-lg max-w-6xl w-full p-6 relative">
                    <button
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
                        onClick={onClose}
                    >
                        ✕
                    </button>
                    {children}
                </div>
            </div>
        );
    }

    /* =========================
       Render
    ========================= */

    return (
        <div className="flex h-screen">
            {/* HEADER */}
            <header className="fixed top-0 left-0 right-0 h-16 bg-white shadow flex items-center justify-between px-4 z-20">
                <div className="flex items-center">
                    {pages.length > 1 && (
                        <button
                            className="p-2 rounded hover:bg-gray-100"
                            onClick={() => setMenuOpen(!menuOpen)}
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    )}

                    <h1 className="ml-4 font-bold text-lg">
                        {selectedPage ? selectedPage.title : "Dashboard"}
                    </h1>
                </div>

                {/* MODULES */}
                <nav className="flex items-center gap-2 overflow-x-auto max-w-[55%]">
                    {selectedPage?.modules?.map((module) => (
                        <button
                            key={module.id}
                            onClick={() => {
                                const stored = getModuleFromStorage(module.id);
                                const moduleToUse = stored ?? module;
                                saveModuleToStorage(moduleToUse);
                                setSelectedModule(moduleToUse);
                                setEditItem(moduleToUse);
                            }}
                            className={`px-3 py-1 rounded-full border text-sm whitespace-nowrap
                                ${selectedModule?.id === module.id
                                    ? "bg-blue-500 text-white border-blue-600"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                        >
                            {module.title || module.overlay_name}
                        </button>
                    ))}
                </nav>
            </header>

            {/* SIDEBAR */}
            <aside className={`fixed top-16 left-0 h-[calc(100%-4rem)] w-80 bg-white shadow-lg transition-transform duration-300 z-10
                ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}>
                <div className="p-4 overflow-y-auto h-full">
                    <h3 className="font-bold text-lg mb-2">Pages</h3>
                    <ul>
                        {pages.map((page) => (
                            <li
                                key={page.id}
                                className={`p-2 rounded cursor-pointer hover:bg-gray-100
                                    ${selectedPage?.id === page.id ? "bg-gray-200 font-semibold" : ""}`}
                                onClick={() => {
                                    setSelectedPage(page);
                                    setMenuOpen(false);
                                }}
                            >
                                {page.title}
                            </li>
                        ))}
                    </ul>
                </div>
            </aside>

            {/* MAIN */}
            <main className="flex-1 mt-16 overflow-y-auto">
                {!selectedModule && (
                    <div className="text-gray-600 text-center italic mt-10">
                        <p>Bienvenue dans la maintenance de votre site.</p>
                        <p>Sélectionnez une partie à modifier.</p>
                    </div>
                )}

                {selectedModule && (() => {
                    const OverlayComponent = getOverlayComponent(selectedModule.overlay_name);

                    return (
                        <div className="bg-gray-300 py-5">
                            <h2 className="text-center pb-5">Aperçu</h2>
                            <OverlayComponent module={selectedModule} />
                        </div>
                    );
                })()}


                {selectedModule && (
                    <div className="p-4 mb-4 rounded-lg">

                        <div className="text-center mt-4">
                            <button
                                className="px-4 py-2 bg-blue-500 text-white rounded"
                                onClick={() => setShowEditModal(true)}
                            >
                                Faire des modifications
                            </button>
                        </div>


                        <div className="text-center mt-4">
                            <button
                                onClick={applyChangesToDatabase}
                                className="px-4 py-2 bg-green-500 text-white rounded"
                            >
                                Valider les modifications
                            </button>
                        </div>
                    </div>
                )
                }

            </main >

            {selectedModule && editItem && (
                <Modal open={showEditModal} onClose={() => setShowEditModal(false)}>
                    <h3 className="text-xl font-bold mb-4">
                        Modifier {selectedModule.title || selectedModule.overlay_name}
                    </h3>
                    <ItemForm
                        type={selectedModule.type}
                        item={editItem}
                        onSubmit={(data) => {
                            updateModule(selectedModule.id, data);
                        }}
                        onCancel={() => setShowEditModal(false)}
                    />
                </Modal>
            )}
        </div >
    );
}
